import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

/**
 * A custom element that manages a product hotspot dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The hotspot button.
 * @property {HTMLAnchorElement} productLink - The product link.
 *
 * @extends Component<Refs>
 */
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];

  /** @type {(() => void) | null} */
  #pointerenterHandler = null;

  /** @type {number | null} */
  timer = null;

  /** @type {number | null} */
  closeTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Open the mobile quick-add modal.
   *
   * @returns {void}
   */
  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (
      this.querySelector('quick-add-component')
    );

    if (!quickAddComponent) return;

    quickAddComponent.handleClick(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  /**
   * Cancel a pending dialog close.
   *
   * This allows the pointer to cross the space between the hotspot
   * trigger and the product popup without the popup disappearing.
   *
   * @returns {void}
   */
  #cancelClose = () => {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  /**
   * Set up desktop hover listeners.
   *
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      this.#cancelClose();

      if (this.refs.dialog.open) return;

      if (this.timer !== null) {
        clearTimeout(this.timer);
      }

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.showDialog();
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;

    trigger.addEventListener('pointerenter', pointerenterHandler);
    trigger.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  /**
   * Remove desktop event listeners.
   *
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger, dialog } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      this.#pointerenterHandler = null;
    }

    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    dialog.removeEventListener('pointerenter', this.#cancelClose);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);

    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.#cancelClose();
  }

  /**
   * Handle breakpoint changes.
   *
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the desktop hotspot dialog.
   *
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    /*
     * Temporarily display the dialog invisibly so that its dimensions
     * can be measured before deciding where to place it.
     */
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else if (dialogStartY < CONTAINER_GAP) {
        verticalOffset = CONTAINER_GAP - dialogStartY;
        y = 'bottom';
      }
    } else if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
      y = 'bottom';
    } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
      y = 'top';
    } else {
      y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Handle the pointer leaving either the hotspot or its dialog.
   *
   * Closing is delayed briefly so the pointer can cross the gap between
   * the hotspot and popup. Entering either element cancels the close.
   *
   * @returns {void}
   */
  #handlePointerLeave = () => {
    const { dialog, trigger } = this.refs;

    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    this.#cancelClose();

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;

      const triggerIsHovered = trigger.matches(':hover');
      const dialogIsHovered = dialog.matches(':hover');

      if (!triggerIsHovered && !dialogIsHovered) {
        this.closeDialog();
      }
    }, 180);
  };

  /**
   * Get the product link for the hotspot product.
   *
   * @returns {HTMLAnchorElement | null}
   */
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  /**
   * Handle a hotspot click.
   *
   * Mobile opens the quick-add modal.
   * Desktop opens the small hotspot popup.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  handleHotspotClick = (event) => {
    if (isMobileBreakpoint()) {
      event.preventDefault();
      event.stopPropagation();
      this.#openQuickAddModal();
      return;
    }

    this.showDialog();
  };

  /**
   * Show the desktop hotspot dialog.
   *
   * @returns {Promise<void>}
   */
  showDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelClose();

    if (dialog.open) return;

    await this.#calculateDialogPlacement();

    dialog.dataset.showing = 'true';
    dialog.show();

    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);

    dialog.addEventListener('pointerenter', this.#cancelClose);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
  };

  /**
   * Close the desktop hotspot dialog.
   *
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#cancelClose();

    if (!dialog.open) return;

    dialog.dataset.closing = 'true';
    dialog.close();

    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);

    dialog.removeEventListener('pointerenter', this.#cancelClose);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);

    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((animation) => animation.finished));

    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Close the dialog when clicking outside it.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Close the dialog using the keyboard or when focus leaves it.
   *
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;

    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);